/**
 * pages/hazard/OpenBoard.js — НЭЭЛТТЭЙ АЮУЛ / ЗӨРЧЛИЙН САМБАР.
 *
 * Route: /hazard/board
 *
 * ЗОРИЛГО: ажилтан хэн ч байсан бүртгэгдсэн, ХАРААХАН ШИЙДЭГДЭЭГҮЙ аюул,
 * зөрчлийг хараад — өөрөө шийдэх боломжтой бол сонгож, авсан арга хэмжээгээ
 * бүртгэнэ. Дараа нь удирдлага (АШУА) баталгаажуулна (/hazard/verify).
 *
 * ⚠ ЭНЭ ЖАГСААЛТ НЬ ҮЙЛДВЭР ДАЯАР. Үндсэн жагсаалт (`getHazards`) нь
 *   удирдлагын түвшнээр хумигддаг тул энгийн ажилтан зөвхөн ӨӨРИЙНХӨӨ
 *   бүртгэлийг хардаг — тэгвэл «бусдын мэдээлсэн аюулыг олж засах» гэсэн
 *   энэ процесс огт ажиллахгүй. Тиймээс тусдаа endpoint
 *   (`/api/mobile/hazard/open-board`) ашиглана: зөвхөн ШИЙДЭГДЭЭГҮЙ мөр,
 *   нэрээ нууцалсан мэдээлэгчийн нэр серверт МАСКЛАГДСАН.
 *
 * ⚠ Ажил зогсоолт (stop_work) энд ГАРАХГҮЙ — түүнд ажил сэргээх тусдаа
 *   шатлал (SWA) бий.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import { getOpenBoard, selfFixHazard, uploadHazardPhoto } from "src/api/hazard";
import { SEV_COLOR } from "src/components/hazard";
import { SearchInput } from "src/components/filters";
import PageHelp from "src/components/help/PageHelp";

const PAGE_BG = "#F1F3F6";
const { TextArea } = Input;

// ⚠ ГУРВАН ТҮВШИН (ХЭМАБМТ-02 журам 4.3.6). Хуучин 4/5-тай мөрүүд нь
//   «Өндөр» шүүлтэд ОРОХГҮЙ тул баазын хөрвүүлэлт ЗААВАЛ ажиллана —
//   SQL/2026-09-16_severity_3_tier.sql
const SEV_OPTIONS = [
  { value: "", label: "Бүх түвшин" },
  { value: "3", label: "3 · Өндөр" },
  { value: "2", label: "2 · Дунд" },
  { value: "1", label: "1 · Бага" },
];

/* ───────────────────────── Шийдсэн бүртгэх цонх ───────────────────────── */
function SelfFixModal({ row, onClose, onSaved }) {
  const [action, setAction] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAction("");
    setPhotos([]);
  }, [row?.id]);

  const submit = async () => {
    const text = action.trim();
    if (!text) return message.warning("Авсан арга хэмжээгээ бичнэ үү.");
    setBusy(true);
    try {
      await selfFixHazard(row.id, text, photos);
      message.success("Бүртгэлээ. Удирдлагын баталгаажуулалт хүлээгдэж байна.");
      onSaved();
      onClose();
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Хадгалж чадсангүй.");
    } finally {
      setBusy(false);
    }
  };

  /** Зураг — MinIO руу шууд. Бүртгэл рүү нь backend `is_before=false` гэж хадгална. */
  const upload = async ({ file, onSuccess, onError }) => {
    try {
      const r = await uploadHazardPhoto(file);
      setPhotos((p) => [
        ...p,
        {
          file_url: r.file_url,
          file_name: r.file_name || file.name,
          file_size: r.file_size,
          content_type: r.content_type || file.type,
        },
      ]);
      onSuccess(r);
    } catch (ex) {
      message.error("Зураг байршуулж чадсангүй.");
      onError(ex);
    }
  };

  return (
    <Modal
      open={!!row}
      title="Би өөрөө шийдлээ"
      onCancel={onClose}
      onOk={submit}
      okText="Илгээх"
      cancelText="Болих"
      confirmLoading={busy}
      width={640}
    >
      {row ? (
        <>
          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E6E9EF",
              borderRadius: 8,
              padding: "8px 10px",
              marginBottom: 12,
              fontSize: 12.5,
            }}
          >
            <b>{row.report_number}</b>
            <span style={{ color: "#8A94A6" }}>
              {" "}
              · {row.hazard_category_name || "ангилалгүй"} ·{" "}
              {row.location_text || "байршил тодорхойгүй"}
            </span>
            <div style={{ marginTop: 4 }}>{row.description || "—"}</div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>
            Авсан арга хэмжээ <b style={{ color: "#DC2626" }}>*</b>
          </div>
          <TextArea
            rows={4}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Юу хийж арилгасан бэ — тодорхой бичнэ (жишээ: тасарсан хамгаалалтын хашлагыг сольж бэхэлсэн)"
          />
          <div style={{ fontSize: 10.5, color: "#9AA3B0", marginTop: 3 }}>
            Энэ текстийг удирдлага уншиж баталгаажуулна. Дараах байдлын зураг
            хавсаргавал баталгаажуулалт хурдан болно.
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, margin: "12px 0 3px" }}>
            Нотлох зураг ({photos.length})
          </div>
          <Upload
            listType="picture-card"
            customRequest={upload}
            accept="image/*"
            onRemove={(f) =>
              setPhotos((p) => p.filter((x) => x.file_name !== f.name))
            }
          >
            <div>
              <PlusOutlined />
              <div style={{ fontSize: 11, marginTop: 4 }}>Зураг</div>
            </div>
          </Upload>

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 8 }}
            message="Баталгаажсаны дараа урамшууллын үзүүлэлтэд тоологдоно"
            description="Илгээмэгц бүртгэл «Баталгаажуулалт хүлээж буй» төлөвт орно. Удирдлага буцаавал шалтгааныг нь хараад дахин засаж болно."
          />
        </>
      ) : null}
    </Modal>
  );
}

/* ──────────────────────────── Үндсэн хуудас ───────────────────────────── */
export default function OpenBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState("");
  const [fixing, setFixing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getOpenBoard({
      page,
      per_page: pageSize,
      search: q || undefined,
      severity: severity || undefined,
    })
      .then(setData)
      .catch((e) =>
        message.error(
          e?.response?.data?.message || "Жагсаалт татаж чадсангүй",
        ),
      )
      .finally(() => setLoading(false));
  }, [page, pageSize, q, severity]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => data?.items || [], [data]);

  const columns = [
    {
      title: "Дугаар",
      dataIndex: "report_number",
      width: 150,
      render: (v, r) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontFamily: "monospace", fontSize: 11.5 }}
          onClick={() =>
            navigate("/hazard/" + r.id, { state: { from: "/hazard/board" } })
          }
        >
          {v}
        </Button>
      ),
    },
    {
      title: "Түвшин",
      dataIndex: "severity",
      width: 96,
      align: "center",
      render: (v, r) => (
        <Tag color={SEV_COLOR[v] ? undefined : "default"}
          style={{
            marginRight: 0,
            color: "#fff",
            background: SEV_COLOR[v] || "#8A94A6",
            border: "none",
          }}
        >
          {r.severity_label || v}
        </Tag>
      ),
    },
    {
      title: "Тайлбар",
      dataIndex: "description",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 12.5 }}>{v || "—"}</div>
          <div style={{ fontSize: 11, color: "#9AA3B0" }}>
            {r.hazard_category_name || "ангилалгүй"}
            {r.location_text ? " · " + r.location_text : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Нэгж",
      dataIndex: "insert_bn_name",
      width: 160,
      render: (v, r) => (
        <span style={{ fontSize: 11.5 }}>
          {v || r.insert_dept_name || "—"}
        </span>
      ),
    },
    {
      title: "Мэдээлсэн",
      dataIndex: "insert_user_name",
      width: 130,
      render: (v) => <span style={{ fontSize: 11.5 }}>{v || "—"}</span>,
    },
    {
      title: "Огноо",
      dataIndex: "report_date",
      width: 110,
      render: (v) => (
        <span style={{ fontSize: 11.5 }}>{(v || "").slice(0, 10)}</span>
      ),
    },
    {
      title: "Үйлдэл",
      width: 120,
      align: "center",
      render: (_v, r) =>
        r.can_self_fix ? (
          <Button
            size="small"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => setFixing(r)}
          >
            Шийдэх
          </Button>
        ) : (
          <Tooltip title={r.self_fix_blocked_reason || "Боломжгүй"}>
            <span style={{ color: "#C4C9D1", fontSize: 11.5 }}>—</span>
          </Tooltip>
        ),
    },
  ];

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%", padding: 16 }}>
      {/* Толгой */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E6E9EF",
          borderRadius: 12,
          padding: "14px 16px",
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          size="small"
          onClick={() => navigate(-1)}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            className="text-xl font-bold text-gray-900 m-0"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <SafetyCertificateOutlined style={{ color: "#16A34A" }} />
            Нээлттэй аюул — өөрөө шийдэх
            <PageHelp topic="hazard/board" />
          </h1>
          <div className="text-sm text-gray-500 mt-1">
            Шийдэгдээгүй бүртгэлээс өөрийн боломжоор арилгаж чадахыг нь сонгож,
            авсан арга хэмжээгээ бүртгэнэ — удирдлага баталгаажуулна
          </div>
        </div>
        <Button
          onClick={() => navigate("/hazard/verify")}
          icon={<SafetyCertificateOutlined />}
        >
          Баталгаажуулалт
        </Button>
      </div>

      {data && data.columns_ready === false ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Бааз бэлэн болоогүй"
          description="hazard_reports хүснэгт дээр self_fix_* / verify_* багана нэмэгдээгүй тул «Шийдэх» үйлдэл алдаа буцаана. ХАБЭА-ийн хөгжүүлэгчид хандана уу."
        />
      ) : null}

      {/* Шүүлт */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <SearchInput
          value={q}
          size="small"
          // Хайлт СЕРВЕР талд — товшилт бүрд дуудвал жагсаалт анивчина
          debounce={400}
          onChange={(v) => {
            setPage(1);
            setQ(v);
          }}
          placeholder="Дугаар, тайлбар, байршлаар хайх"
          style={{ width: 280, flex: "0 0 auto" }}
        />
        <Select
          size="small"
          value={severity}
          onChange={(v) => {
            setPage(1);
            setSeverity(v);
          }}
          options={SEV_OPTIONS}
          style={{ width: 150 }}
        />
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>
          Шинэчлэх
        </Button>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#8A94A6" }}>
          Нээлттэй нийт <b>{data?.total ?? 0}</b>
        </span>
      </div>

      <Card size="small" bodyStyle={{ padding: 0 }}>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          locale={{
            emptyText: <Empty description="Нээлттэй бүртгэл алга" />,
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            size: "small",
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <SelfFixModal
        row={fixing}
        onClose={() => setFixing(null)}
        onSaved={load}
      />

      <div style={{ marginTop: 10, fontSize: 11, color: "#9AA3B0" }}>
        <Space size={6}>
          <span>
            Ажил зогсоолт болон хаагдсан бүртгэл энд харагдахгүй — тэдгээр нь
            өөрийн урсгалаар шийдэгдэнэ.
          </span>
        </Space>
      </div>
    </div>
  );
}
